"use client"

import { useState, useEffect, useRef, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { FertilizerForm, FERTILIZER_RESULT_ID } from "@/components/tools/fertilizer-form"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultSuccessCard, ResultEmailCaptureCard, ToolPageLoadingFallback, ToolPageShell, ToolWideRegion } from "@/components/tools/shared"
import type { ResultSuccessCardStat } from "@/components/tools/shared"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import type { FertilizerResult } from "@/lib/fertilizer-calculator"
import { Layers, DollarSign, MapPin } from "lucide-react"

import { EmbedCodeModal } from "@/components/embed/embed-code-modal"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { FERTILIZER_TYPES } from "@/data/fertilizer-types"
import { useDefaultLawn, getCurrentSeason } from "@/hooks/use-default-lawn"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { FertilizerCoverageDiagram } from "@/components/tools/shared/fertilizer-coverage-diagram"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { CuratedProductPicks } from "@/components/tools/shared/curated-product-picks"
import { getFertilizerPicks } from "@/lib/fertilizer-calculator/product-picks"
import { useFromTool } from "@/hooks/use-from-tool"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"

function FertilizerCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("fertilizer-calculator")
  // calculator_started on first input — calculator_used is gated on a resolved
  // ZIP (zipKnown), so it can't measure pre-ZIP abandonment.
  const startedRef = useCalculatorStarted('fertilizer')
  const {
    lawn: defaultLawn,
    lawns,
    selectedLawnId,
    setSelectedLawnId,
    prefill: lawnPrefill,
    isLoading: isLoadingLawn,
  } = useDefaultLawn()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } =
    useNewsletterPrompt()
  const [formData, setFormData] = useState<any>(null)
  const [liveResult, setLiveResult] = useState<FertilizerResult | null>(null)
  const trackedUsageRef = useRef(false)

  useEffect(() => {
    if (!liveResult || trackedUsageRef.current) return
    trackedUsageRef.current = true
    trackUsage("fertilizer-calculator")
    analytics.calculatorUsed("fertilizer", {
      inputs: {
        grass_type: liveResult.grassType,
        fertilizer_type: liveResult.fertilizerType,
        lawn_size_sqft: liveResult.lawnSize,
        season: liveResult.season,
        application_goal: liveResult.applicationGoal,
      },
      result: {
        pounds: liveResult.fertilizerAmount.pounds,
        bags: liveResult.fertilizerAmount.bags,
        bag_size: liveResult.fertilizerAmount.bagSize,
        nitrogen_lbs_per_1000sqft: liveResult.npkRequirements.nitrogen,
      },
      source: "live_update",
    })
  }, [liveResult, trackUsage])

  // URL prefill — the form is fully live, so we just seed initialValues.
  useEffect(() => {
    const grassParam = searchParams.get("grass")
    const sizeParam = searchParams.get("size")
    const zipParam = searchParams.get("zip")
    const seasonParam = searchParams.get("season")
    const typeParam = searchParams.get("type")
    const sunlightParam = searchParams.get("sunlight")

    if (grassParam || sizeParam || zipParam || sunlightParam) {
      let grassType = ""
      if (grassParam) {
        // Treat dashes/underscores/periods as separators on both sides so guide
        // slugs round-trip (e.g. "st-augustinegrass" -> key "St. Augustinegrass").
        const norm = (s: string) => s.toLowerCase().replace(/[-_.]+/g, " ").replace(/\s+/g, " ").trim()
        const normalized = norm(grassParam)
        const matched = Object.keys(GRASS_CHARACTERISTICS).find((n) => norm(n) === normalized)
        grassType = matched || ""
      }
      const lawnSize = sizeParam ? parseInt(sizeParam, 10) : undefined

      const validSeasons = ["spring", "summer", "fall", "winter"] as const
      const season = seasonParam && (validSeasons as readonly string[]).includes(seasonParam.toLowerCase())
        ? (seasonParam.toLowerCase() as (typeof validSeasons)[number])
        : undefined

      const validTypes: Record<string, string> = {
        synthetic: "balanced-10-10-10",
        organic: "organic-6-2-0",
        "slow-release": "slow-release-15-5-10",
      }
      const normalizedType = typeParam?.toLowerCase()
      const fertilizerType = normalizedType && FERTILIZER_TYPES[normalizedType]
        ? normalizedType
        : normalizedType
          ? validTypes[normalizedType]
          : undefined

      const prefilled = {
        zipCode: zipParam && /^\d{5}$/.test(zipParam) ? zipParam : undefined,
        grassType: grassType || undefined,
        lawnSize: lawnSize && !isNaN(lawnSize) ? lawnSize : undefined,
        sunlight: sunlightParam ?? undefined,
        season,
        fertilizerType,
      }
      setFormData(prefilled)
    }
  }, [searchParams])

  // Pre-fill from saved lawn
  useEffect(() => {
    const hasUrlParams = searchParams.get("grass") || searchParams.get("size") || searchParams.get("zip") || searchParams.get("sunlight")
    if (hasUrlParams || formData || isLoadingLawn) return

    if (defaultLawn) {
      const prefilled: { zipCode?: string; grassType?: string; lawnSize?: number; season?: string; soilType?: string; sunlight?: string } = {
        season: getCurrentSeason(),
      }
      if (lawnPrefill.zipCode) prefilled.zipCode = lawnPrefill.zipCode
      if (lawnPrefill.grassType) {
        const matched = Object.keys(GRASS_CHARACTERISTICS).find(
          (n) => n.toLowerCase() === lawnPrefill.grassType?.toLowerCase(),
        )
        if (matched) prefilled.grassType = matched
      }
      if (lawnPrefill.lawnSizeSqft) prefilled.lawnSize = lawnPrefill.lawnSizeSqft
      if (lawnPrefill.soilType) prefilled.soilType = lawnPrefill.soilType
      if (lawnPrefill.sunlight) prefilled.sunlight = lawnPrefill.sunlight

      if (prefilled.grassType || prefilled.lawnSize || prefilled.zipCode || prefilled.soilType || prefilled.sunlight) {
        setFormData(prefilled)
      }
    }
  }, [defaultLawn, isLoadingLawn, searchParams, formData, lawnPrefill])

  // Result-matched fertilizer picks once a live result exists; the generic
  // "popular fertilizers" block shows before the user has a result.
  const fertilizerPicks = liveResult ? getFertilizerPicks(liveResult) : []

  return (
    <ToolPageShell
      breadcrumbLabel="Fertilizer Calculator"
      rootRef={startedRef}
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Fertilizer Calculator"
          usageCount={usageCount}
          grassType={liveResult?.grassType}
          zipCode={formData?.zipCode ?? lawnPrefill.zipCode}
        />
      }
    >
      {/* Hero */}
      <div className="relative">
        <div className="absolute top-0 right-0 hidden md:block">
          <EmbedCodeModal
            toolName="Fertilizer Calculator"
            toolSlug="fertilizer-calculator"
            hasMiniVersion={true}
          />
        </div>
        <HeadlineHero
          eyebrow="Built for your grass + ZIP"
          eyebrowIcon={<MapPin className="h-3.5 w-3.5" aria-hidden="true" />}
          title="How much should you"
          accent="feed?"
          subtitle="Free lawn fertilizer calculator. Pounds of nitrogen, phosphorus, and potassium per 1000 sq ft based on your bag's NPK and lawn size. Bag count and a 12-month feeding calendar fill in as you go."
        />
      </div>

      <LawnPrefillBanner
        lawns={lawns}
        selectedLawnId={selectedLawnId}
        onSelect={(id) => {
          setSelectedLawnId(id)
          setFormData(null)
        }}
      />

      {/* Form with live preview — the only results UI on this page. Its
          two-column layout starts at 1200px and needs the 6xl width. */}
      <ToolWideRegion>
        <FertilizerForm initialValues={formData} onResultChange={setLiveResult} />
      </ToolWideRegion>

      {liveResult && (
        <div id={FERTILIZER_RESULT_ID} className="mt-8 scroll-mt-20 space-y-6">
          <FertilizerSuccessCard
            result={liveResult}
            prefill={{
              zip: formData?.zipCode ?? lawnPrefill.zipCode,
              soilType: formData?.soilType ?? lawnPrefill.soilType,
              sunlight: formData?.sunlight ?? lawnPrefill.sunlight,
            }}
          />
          <ResultEmailCaptureCard
            source="tool_result_fertilizer"
            toolDisplayName="fertilizer schedule"
            subcopy={`Your 12-month ${liveResult.grassType} lawn calendar, starting with this month's tasks. Arrives right away.`}
            grassType={liveResult.grassType}
            zipCode={formData?.zipCode ?? lawnPrefill.zipCode}
            accountLead={{
              redirectTo: buildToolUrl("fertilizer-calculator", {
                grassType: liveResult.grassType,
                zip: formData?.zipCode ?? lawnPrefill.zipCode,
                lawnSize: liveResult.lawnSize,
                season: liveResult.season,
              }),
              headline: `Save my fertilizer plan for ${liveResult.grassType}`,
              subcopy: "Keep this schedule in a free account and track your lawn over time.",
              dividerLabel: "or get your 12-month calendar by email",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_fertilizer"),
            }}
            onCaptured={() => {
              closeNewsletterModal()
            }}
          />
          {liveResult.grassType !== "Carpetgrass" && (
            <ProductGuideCta source="fertilizer_calculator_result" variant="result" />
          )}
          {/* Peak-end product placement: the grass-matched fertilizer is the
              exact thing the user just calculated they need. Render it here,
              inside the result block (above RelatedTools), not buried at the
              page bottom. This is the fertilizer-calculator CTR fix. */}
          {fertilizerPicks.length > 0 ? (
            <CuratedProductPicks
              picks={fertilizerPicks}
              title="Fertilizer for your lawn"
              description={`Matched to ${liveResult.grassType}`}
              trackingSlug="tool:fertilizer-calculator"
            />
          ) : liveResult.grassType !== 'Carpetgrass' ? (
            <ProductRecommendations
              categories={["Fertilizers & Soil Health"]}
              title="Popular lawn fertilizers"
              trackingContext="fertilizer-calculator-result"
              compact
            />
          ) : null}
        </div>
      )}

      {/* Pre-submission affiliate surface — mirrors herbicide-timing (5.76% CTR):
          show fertilizer picks in the empty state so the visitors who never
          complete the calc still see products high on the page, above
          RelatedTools. Post-result, the grass-matched picks render at the peak
          inside the result block above. */}
      {!liveResult && (
        <div className="mt-8">
          <ProductRecommendations
            categories={["Fertilizers & Soil Health"]}
            title="Popular lawn fertilizers"
            trackingContext="fertilizer-calculator-pre-submission"
            compact
          />
        </div>
      )}

      <RelatedTools
        className="mt-10"
        currentToolId="fertilizer-calculator"
        prefillValues={{
          zip: formData?.zipCode ?? lawnPrefill.zipCode,
          grassType: liveResult?.grassType ?? formData?.grassType ?? lawnPrefill.grassType,
          lawnSize: liveResult?.lawnSize ?? formData?.lawnSize ?? lawnPrefill.lawnSizeSqft,
          soilType: formData?.soilType ?? lawnPrefill.soilType,
          sunlight: formData?.sunlight ?? lawnPrefill.sunlight,
          season: liveResult?.season ?? formData?.season,
          fertilizerType: formData?.fertilizerType,
        }}
      />
    </ToolPageShell>
  )
}

const SEASON_LABELS: Record<FertilizerResult["season"], string> = {
  spring: "this spring",
  summer: "this summer",
  fall: "this fall",
  winter: "this winter",
}

/**
 * Bounded peak-moment frame for the fertilizer result: total product to spread
 * (hero) + a one-sentence interpretation naming the user's grass, size, and
 * season, + one primary next-action (plan watering). Winter applications on
 * cool-season / dormant warm-season grass return 0 lbs by design, so the card
 * reframes that as a deliberate skip rather than an empty number.
 */
function FertilizerSuccessCard({
  result,
  prefill,
}: {
  result: FertilizerResult
  prefill: { zip?: string | null; soilType?: string | null; sunlight?: string | null }
}) {
  const pounds = result.fertilizerAmount.pounds
  const seasonLabel = SEASON_LABELS[result.season]
  const sizeLabel = result.lawnSize.toLocaleString()
  const skipSeason = pounds <= 0
  const isCarpetgrass = result.grassType === "Carpetgrass"

  // Single primary next-action: water in the fertilizer at the right depth.
  // Reuse the same prefilled-URL builder the RelatedTools grid uses so the
  // user's grass / ZIP / size carry into the watering schedule.
  const wateringHref = buildRelatedToolUrl("watering-schedule", "fertilizer-calculator", {
    zip: prefill.zip,
    grassType: result.grassType,
    lawnSize: result.lawnSize,
    soilType: prefill.soilType,
    sunlight: prefill.sunlight,
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "fertilizer-calculator",
      targetTool: "watering-schedule",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Water in your fertilizer at the right depth",
      prefillFields: ["zip", "grass", "size"]
        .filter((f) => (f === "zip" ? Boolean(prefill.zip) : true))
        .join(","),
    })
  }
  const cta = isCarpetgrass
    ? { href: "/guides/carpetgrass#watering", label: "Read Carpetgrass watering guidance" }
    : { href: wateringHref, label: "Plan your watering schedule", onClick: handleCtaClick }

  const share = {
    // Only carry params the fertilizer page-content reads back from the URL
    // into the form's initialValues. result.fertilizerType is a display name
    // ("10-10-10 Balanced Fertilizer"), not a FertTypeId the form can parse,
    // so it's dropped rather than round-trip to the wrong (default) type.
    url: buildToolUrl("fertilizer-calculator", {
      zip: prefill.zip,
      grassType: result.grassType,
      lawnSize: result.lawnSize,
      season: result.season,
      sunlight: prefill.sunlight,
    }),
    title: "Fertilizer Plan",
    text: `Fertilizer plan for my ${result.grassType} lawn`,
    trackingId: "fertilizer-calculator",
  }

  if (skipSeason) {
    return (
      <ResultSuccessCard
        eyebrow="Skip this season"
        interpretation={
          <>
            Hold off feeding your {sizeLabel} sq ft {result.grassType} lawn {seasonLabel}. {result.nextApplication}
          </>
        }
        cta={cta}
        share={share}
      />
    )
  }

  const poundsStr = pounds.toLocaleString(undefined, { maximumFractionDigits: 1 })
  const bagsCount = result.fertilizerAmount.bags
  const bagSize = result.fertilizerAmount.bagSize

  // The diagram shows purchase and application quantities. Secondary metrics
  // retain the engine's N rate and positive cost estimate.
  const stats: ResultSuccessCardStat[] = [
    {
      icon: Layers,
      label: "N rate / 1000 sqft",
      value: `${result.npkRequirements.nitrogen.toLocaleString(undefined, { maximumFractionDigits: 2 })} lb`,
    },
  ]
  if (result.fertilizerAmount.costEstimate > 0) {
    stats.push({
      icon: DollarSign,
      label: "Est. cost",
      value: `$${Math.round(result.fertilizerAmount.costEstimate).toLocaleString()}`,
    })
  }

  return (
    <ResultSuccessCard
      heroValue={poundsStr}
      heroUnit="lbs"
      heroLabel="of fertilizer to spread"
      stats={stats}
      visual={<FertilizerCoverageDiagram pounds={pounds} bags={bagsCount} bagSize={bagSize} lawnSize={result.lawnSize} />}
      reframe={{
        ariaLabel: "Show fertilizer amount in pounds or bags",
        defaultKey: "lbs",
        analyticsEvent: "fertilizer_view_toggled",
        analyticsProps: { grass_type: result.grassType, season: result.season },
        options: [
          { key: "lbs", label: "Pounds" },
          { key: "bags", label: "Bags" },
        ],
        values: {
          lbs: { value: poundsStr, unit: "lbs", label: "of fertilizer to spread" },
          bags: {
            value: bagsCount.toString(),
            unit: bagsCount === 1 ? "bag" : "bags",
            label: `of ${bagSize}-lb bags needed`,
          },
        },
      }}
      interpretation={
        <>
          Spread about {poundsStr} lbs of {result.fertilizerType} across your {sizeLabel} sq ft {result.grassType} lawn {seasonLabel}, roughly {bagsCount} {bagsCount === 1 ? "bag" : "bags"}.
        </>
      }
      cta={cta}
      share={share}
    />
  )
}

export function FertilizerCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="fertilizer-calculator" />}>
      <SearchParamsHydrationGate dateSensitive>
        <FertilizerCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
